import { useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { ProductContext } from "../../context/ProductContext";
import { useNavigation } from "../../utils/navigation";

const Hero = styled.div`
  width: 100%;
  background-color: var(--bg-200);
  padding-bottom: var(--spacing-xxl);
  display: flex;
  justify-content: center;
`;

const HeroInner = styled.div`
  width: calc(100% - var(--spacing-xl) * 2);
  @media (min-width: 1025px) {
    max-width: var(--max-width-container);
  }
`;

const Eyebrow = styled.p`
  margin: 0 0 var(--spacing-xs) 0;
  font-size: var(--font-size-small);
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--primary-100);
`;

const Title = styled.h1`
  font-size: var(--font-size-xxlarge);
  margin: 0 0 var(--spacing-sm) 0;
  font-weight: 700;
  color: var(--text-100);
`;

const Subtitle = styled.p`
  margin: 0 0 var(--spacing-xl) 0;
  max-width: 560px;
  color: var(--text-200);
  font-size: var(--font-size-base);
`;

const SearchBox = styled.div`
  position: relative;
  max-width: 420px;
`;

const SearchInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 12px var(--spacing-md) 12px 40px;
  font-size: 14px;
  border-radius: var(--border-radius-base);
  border: 1px solid var(--bg-300);
  background-color: var(--bg-100);
  color: var(--text-100);

  &:focus {
    outline: none;
    border-color: var(--primary-100);
    box-shadow: 0 0 0 3px rgba(63, 81, 181, 0.12);
  }
`;

const SearchIcon = styled.svg`
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
`;

const Container = styled.main`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`;

const MaxWidthWrapper = styled.div`
  margin: var(--spacing-xl) 0 var(--spacing-xxl) 0;
  width: calc(100% - var(--spacing-xl) * 2);
  @media (min-width: 1025px) {
    max-width: var(--max-width-container);
  }
`;

const ResultCount = styled.p`
  margin: 0 0 var(--spacing-lg) 0;
  font-size: var(--font-size-small);
  color: var(--text-200);
`;

const LetterGroup = styled.div`
  margin-bottom: var(--spacing-xl);
`;

const LetterHeading = styled.h2`
  margin: 0 0 var(--spacing-md) 0;
  font-size: var(--font-size-large);
  font-weight: 700;
  color: var(--primary-100);
  padding-bottom: var(--spacing-xs);
  border-bottom: 1px solid var(--bg-300);
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--spacing-md);

  @media (min-width: 600px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (min-width: 1025px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const BrandCard = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-md) var(--spacing-md) var(--spacing-md) var(--spacing-lg);
  border-radius: var(--border-radius-base);
  background-color: var(--bg-100);
  border: 1px solid var(--bg-300);
  box-shadow: var(--shadow-small);
  cursor: pointer;
  overflow: hidden;
  transition: transform var(--transition-fast), box-shadow var(--transition-fast),
    border-color var(--transition-fast);

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background-color: ${({ $color }) => $color || "var(--primary-100)"};
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-large);
    border-color: var(--primary-100);
  }
`;

const BrandName = styled.span`
  font-size: var(--font-size-base);
  font-weight: 600;
  color: var(--text-100);
`;

const EmptyState = styled.p`
  color: var(--text-200);
`;

const BrandsMain = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { categories } = useContext(ProductContext);
  const { goToCategory } = useNavigation();
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    if (!categories) return [];
    const q = query.trim().toLowerCase();
    const list = q
      ? categories.filter((c) => c.name.toLowerCase().includes(q))
      : categories;
    return [...list].sort((a, b) => a.name.localeCompare(b.name));
  }, [categories, query]);

  const grouped = useMemo(() => {
    const groups = {};
    filtered.forEach((category) => {
      const first = category.name[0]?.toUpperCase() || "#";
      const letter = /[A-Z]/.test(first) ? first : "#";
      if (!groups[letter]) groups[letter] = [];
      groups[letter].push(category);
    });
    return Object.keys(groups)
      .sort()
      .map((letter) => ({ letter, items: groups[letter] }));
  }, [filtered]);

  return (
    <Container>
      <Hero className="header-top-padding">
        <HeroInner>
          <Eyebrow>SnusCo</Eyebrow>
          <Title>{t("BRANDS.TITLE", "All Brands")}</Title>
          <Subtitle>
            {t(
              "BRANDS.SUBTITLE",
              "Browse every nicotine pouch brand available in our store."
            )}
          </Subtitle>
          <SearchBox>
            <SearchIcon width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path
                d="M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z"
                stroke="var(--text-200)"
                strokeWidth="2"
              />
              <path
                d="M21 21L16.65 16.65"
                stroke="var(--text-200)"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </SearchIcon>
            <SearchInput
              type="text"
              placeholder={t("BRANDS.SEARCH", "Search brands...")}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </SearchBox>
        </HeroInner>
      </Hero>

      <MaxWidthWrapper>
        {categories && categories.length > 0 ? (
          <>
            <ResultCount>
              {filtered.length} {t("BRANDS.RESULTS", "brands")}
            </ResultCount>
            {grouped.map((group) => (
              <LetterGroup key={group.letter}>
                <LetterHeading>{group.letter}</LetterHeading>
                <Grid>
                  {group.items.map((category) => (
                    <BrandCard
                      key={category.id}
                      $color={category.color}
                      onClick={() => navigate(goToCategory(category.name))}
                    >
                      <BrandName>{category.name}</BrandName>
                    </BrandCard>
                  ))}
                </Grid>
              </LetterGroup>
            ))}
            {filtered.length === 0 && (
              <EmptyState>
                {t("BRANDS.NO_RESULTS", "No brands match your search.")}
              </EmptyState>
            )}
          </>
        ) : (
          <EmptyState>{t("BRANDS.EMPTY", "No brands available.")}</EmptyState>
        )}
      </MaxWidthWrapper>
    </Container>
  );
};

export default BrandsMain;
